import {
  ImageFallback,
  Shadow,
  Text,
  Tooltip,
  Icon,
  IconButton,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Button, Box } from 'native-base';
import type { InterfaceStackProps } from 'native-base/lib/typescript/components/primitives/Stack/Stack';
import React from 'react';

import { LateralBar } from '../../../../LateralBar';

interface EquityPendingSubscribeItemProps {
  image?: string;
  productName?: string | null;
  symbol?: string;
  productId: string;
  lateralBarColor: string;
  operationIsSale?: boolean;
  onPressButton?: () => void;
}

const getStackContainerStyle = (
  operationIsSale: boolean,
  onPressButton?: () => void
): InterfaceStackProps => ({
  w: 'full',
  py: Spacing['inline.xxs'],
  px: {
    base: Spacing['inline.xxs'],
    sm: Spacing['inline.xxs'],
  },
  direction: { base: operationIsSale ? 'row' : 'column', lg: 'row' },
  ...(operationIsSale &&
    onPressButton && {
      alignItems: 'center',
      justifyContent: 'space-between',
    }),
});

const ButtonComponent: React.FC<Pick<EquityPendingSubscribeItemProps, 'onPressButton'>> = ({
  onPressButton,
}) =>
  useBreakpointValue({
    base: (
      <IconButton
        icon="ArrowLeft"
        testId="table-equity-buttonSelectProduct"
        iconColor={Color['neutral.white']}
        iconSize="xs"
        backgroundColor={Color['product.rendavariavelsale']}
        borderRadius={BoxRadius.sm}
        h={Spacing['inline.md']}
        w={Spacing['inline.md']}
        shadow={3}
        onPress={onPressButton}
      />
    ),
    lg: (
      <Button
        testID="table-equity-buttonSelectProduct"
        variant="primary"
        bg={Color['product.rendavariavelsale']}
        onPress={onPressButton}>
        Voltar ao Lote
      </Button>
    ),
  });

const ProductInfo: React.FC<
  Omit<EquityPendingSubscribeItemProps, 'lateralBarColor' | 'operationIsSale' | 'productId'> & {
    isMobile: boolean;
  }
> = ({ image, productName, onPressButton, symbol, isMobile }) => (
  <HStack alignItems={'center'} justifyContent={'space-between'}>
    <HStack
      space={Spacing['inline.xxxs']}
      maxWidth={{ base: 'full', sm: '161px', lg: '170px' }}
      width={{ sm: '220px' }}>
      <ImageFallback
        src={image!}
        alt="logo"
        width="54px"
        height="48px"
        testID="product-img"
        resizeMode="cover"
        borderRadius={BoxRadius.sm}
        resizeMethod="scale"
      />
      <Tooltip testID="qa" label={productName || ''}>
        <VStack flex={1}>
          <Text
            variant="semibold"
            fontSize={Typography['fontsize.md']}
            color={'neutral.darkest'}
            noOfLines={1}
            lineHeight="distant">
            {productName}
          </Text>
          <Text
            variant="regular"
            fontSize={Typography['fontsize.md']}
            color={'neutral.darkest'}
            lineHeight="distant">
            {symbol}
          </Text>
        </VStack>
      </Tooltip>
    </HStack>
    {isMobile && onPressButton && <ButtonComponent onPressButton={onPressButton} />}
  </HStack>
);

const ProductAlert: React.FC<
  Pick<EquityPendingSubscribeItemProps, 'operationIsSale' | 'onPressButton'>
> = (props) => (
  <HStack space={Spacing['inline.nano']} flex={1} alignItems="center">
    <Icon testID="icon" name="Info" size="xs" />
    <Text maxW="500px" variant="regular" fontSize={Typography['fontsize.md']} color="neutral.black">
      A {props.operationIsSale ? 'venda' : 'compra'} deste ativo está{' '}
      <Text fontWeight="bold">temporariamente </Text>
      <Text fontWeight="bold">indisponível</Text> devido ao processo de cotação, que pode levar
      alguns minutos. Aguarde enquanto finalizamos a atualização.
    </Text>
  </HStack>
);

export const EquityPendingSubscribeItem: React.FC<EquityPendingSubscribeItemProps> = (props) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const stackContainerStyle = getStackContainerStyle(!!props.operationIsSale, props.onPressButton);

  return (
    <Shadow
      level={3}
      borderRadius={BoxRadius.sm}
      testID={`table-equity-body-${props.productId}`}
      mb={Spacing['inline.quarck']}>
      <LateralBar
        backgroundColor={props.lateralBarColor}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <Stack {...stackContainerStyle}>
        <Stack
          direction={{ base: 'column', sm: props.operationIsSale ? 'column' : 'row', lg: 'row' }}
          alignItems={{ sm: props.operationIsSale ? 'flex-start' : 'center' }}
          justifyContent={{ sm: 'space-between' }}
          space={{ base: Spacing['inline.xs'], lg: Spacing['inline.md'] }}
          flex={1}>
          <ProductInfo {...props} isMobile={isMobile} />
          <ProductAlert {...props} />
        </Stack>
        {!isMobile && props.onPressButton && (
          <ButtonComponent onPressButton={props.onPressButton} />
        )}
        {!isMobile && !props.onPressButton && <Box />}
      </Stack>
    </Shadow>
  );
};
